import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { filter, take, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { NgbModalRef, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import {
  BASE_PERMISSIONS,
  BaseCoreState,
  BaseFormatDecimalPipe,
  BaseRolesService,
  BaseToastAlertClasses,
  CustomerDepartmentQualificationsListItemResponse,
  DepartmentListItemResponse,
  EditInquiryFormInterface,
  InquiryCreateRequestExtended,
  InquiryDate,
  ShiftPlanCalendarRangeInterface,
  UserItemResponse,
  baseFormsActions,
  baseInquiryActions,
  baseInquirySelectors,
  baseSharedActions,
  InquiryCreateRequest,
  TimeAndShiftsFormInterface,
} from 'base';

@Component({
  selector: 'base-customer-shift-plan',
  templateUrl: './base-customer-shift-plan.component.html',
})
export class BaseCustomerShiftPlanComponent implements OnInit, OnDestroy {
  @ViewChild('sideBar', { static: true }) sidebarContent: ElementRef;

  newInquiry: InquiryCreateRequest;
  offcanvas: NgbOffcanvasRef;
  isSidebarVisible = false;
  modalRef: NgbModalRef;
  isEditInquiryFormSubmitted = false;
  canPublishInquiry: boolean;
  editInquiryForm: FormGroup<EditInquiryFormInterface>;
  inquiryId: undefined | number;
  ranges: ShiftPlanCalendarRangeInterface[] = [];

  private destroy$ = new Subject();

  constructor(
    private store: Store<BaseCoreState>,
    private rolesService: BaseRolesService,
    private fb: NonNullableFormBuilder,
    private formatDecimalPipe: BaseFormatDecimalPipe,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.canPublishInquiry = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PUBLISH_INQUIRY);

    this.store
      .select(baseInquirySelectors.getShiftPlanRangeList)
      .subscribe((list: ShiftPlanCalendarRangeInterface[]) => {
        this.ranges = list;
      });

    this.store
      .select(baseInquirySelectors.getShiftPlanList)
      .pipe(
        filter(({ formData }) => !!formData),
        map(({ formData }) => formData as InquiryCreateRequest & { id?: number }),
        take(1)
      )
      .subscribe((data) => {
        this.newInquiry = data;
        this.inquiryId = data.id;
      });

    this.initEditForms();
  }

  ngOnDestroy() {
    this.store.dispatch(baseInquiryActions.resetShiftPlanData());
    this.destroy$.next(true);
  }

  collectInquiryDates(list: ShiftPlanCalendarRangeInterface[]): InquiryDate[] {
    return list.flatMap(({ dates, rangeData }) => {
      return dates
        .filter(({ isVisible }) => isVisible)
        .map(({ dateString }) => ({
          date: dateString,
          shiftZd: rangeData.shifts.zd,
          shiftNd: rangeData.shifts.nd,
          shiftSd: rangeData.shifts.sd,
          shiftFd: rangeData.shifts.fd,
          flexShift: rangeData.flexShift,
          //TODO: property 'position' can be removed
          position: 1,
        }));
    });
  }

  goToPreviousPage() {
    this.store.dispatch(baseFormsActions.clearStaffRequestForm());
    this.location.back();
  }

  publish() {
    this.isEditInquiryFormSubmitted = true;
    if (!this.editInquiryForm.valid) {
      this.editInquiryFormInvalid();
      return;
    }

    if (this.inquiryId) {
      this.store.dispatch(
        baseInquiryActions.updateAndPublishInquiry({
          payload: {
            inquiry: this.inquiryId.toString(),
            body: this.prepareOutput(),
            callback: () => {
              this.goToPreviousPage();
            },
          },
        })
      );
    } else {
      this.store.dispatch(
        baseInquiryActions.createAndPublishInquiry({
          payload: {
            data: this.prepareOutput(),
            callback: () => {
              this.goToPreviousPage();
            },
          },
        })
      );
    }
  }

  saveUnpublishedInquiry() {
    this.isEditInquiryFormSubmitted = true;
    if (!this.editInquiryForm.valid) {
      this.editInquiryFormInvalid();
      return;
    }
    if (this.inquiryId) {
      this.store.dispatch(
        baseInquiryActions.updateCustomerInquiry({
          payload: {
            inquiry: this.inquiryId.toString(),
            body: this.prepareOutput(),
            callback: () => {
              this.goToPreviousPage();
            },
          },
        })
      );
    } else {
      this.store.dispatch(
        baseInquiryActions.createInquiry({
          payload: {
            data: this.prepareOutput(),
            callback: () => {
              this.goToPreviousPage();
            },
          },
        })
      );
    }
  }

  toggleSidebar(): void {
    this.isSidebarVisible = !this.isSidebarVisible;
  }

  private editInquiryFormInvalid(): void {
    if (
      this.editInquiryForm.controls.jobDescription.errors?.required ||
      this.editInquiryForm.controls.mainQualification.errors?.required ||
      this.editInquiryForm.controls.responsible.errors?.required
    ) {
      this.store.dispatch(
        baseSharedActions.addSystemAlert({
          payload: {
            class: BaseToastAlertClasses.error,
            body: 'validation.required',
          },
        })
      );
      if (window.innerWidth <= 2000) {
        this.toggleSidebar();
      }
    }
  }

  private initEditForms(): void {
    this.editInquiryForm = this.fb.group({
      unlimited: [false],
      dates: [[] as string[]],
      jobDescription: ['', [Validators.required]],
      weeklyWorkingHours: this.fb.control(''),
      requirements: [[] as number[]],
      customerDepartment: [null as DepartmentListItemResponse | null],
      mainQualification: [null as CustomerDepartmentQualificationsListItemResponse | null, [Validators.required]],
      qualifications: [[] as number[]],
      responsible: [null as (UserItemResponse & { fullName: string }) | null, [Validators.required]],
      costUnit: [null as string | null, [Validators.maxLength(255)]],
      workEquipmentPsp: [null as string | null, [Validators.maxLength(255)]],
      workEquipmentCustomer: [null as string | null, [Validators.maxLength(255)]],
      remark: [null as string | null],
      timeShiftsForm: this.fb.array([] as FormGroup<TimeAndShiftsFormInterface>[]),
    });
  }

  private prepareOutput(): InquiryCreateRequestExtended {
    const editInquiryFormValue = this.editInquiryForm.getRawValue();

    return {
      unlimited: editInquiryFormValue.unlimited,
      dates: this.collectInquiryDates(this.ranges),
      shiftTimes: editInquiryFormValue.timeShiftsForm,
      mainQualification: editInquiryFormValue.mainQualification?.id as number,
      customerDepartment: editInquiryFormValue.customerDepartment?.id as number,
      responsible: editInquiryFormValue.responsible?.party as number,
      weeklyWorkingHours: this.formatDecimalPipe.transform(editInquiryFormValue.weeklyWorkingHours).replace(',', '.'),
      requirements: editInquiryFormValue.requirements || [],
      qualifications: editInquiryFormValue.qualifications || [],
      jobDescription: editInquiryFormValue.jobDescription,
      costUnit: editInquiryFormValue.costUnit || null,
      workEquipmentCustomer: editInquiryFormValue.workEquipmentCustomer || null,
      workEquipmentPsp: editInquiryFormValue.workEquipmentPsp || null,
      remark: editInquiryFormValue.remark || null,
    };
  }
}
